import {IFunctionContainer} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/ClientFunction';
import {Function, Param} from '@mintdata/mint-kit/api/decorators/Function';
import {SheetType} from '@mintdata/mint-kit/api/functions/SheetAPI';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {IValue} from '@mintdata/mint-kit/api/types/Value';
import {ABoolean} from '@mintdata/mint-kit/api/values/ABoolean';
import {AError} from '@mintdata/mint-kit/api/values/AError';
import {AInteger} from '@mintdata/mint-kit/api/values/AInteger';
import {ANumber} from '@mintdata/mint-kit/api/values/ANumber';
import {AString} from '@mintdata/mint-kit/api/values/AString';
import {EvalErrors} from '@mintdata/mint-kit/api/values/errors/EvalErrors';
import {AFrame} from '@mintdata/mint-kit/api/values/frames/AFrame';
import {IFrame} from '@mintdata/mint-kit/api/values/frames/Frame';
import {FrameField} from '@mintdata/mint-kit/api/values/frames/FrameField';
import {Frames} from '@mintdata/mint-kit/api/values/frames/Frames';
import {CoercionUtils} from '@mintdata/mint-kit/impl/coercionUtils/CoercionUtils';
import bind from 'bind-decorator';

export class MathFunctions implements IFunctionContainer
{
    public get namespace()
    {
        return 'math';
    }

    /*    @Function({
            title: 'sum(a,b)',
            alias: 'SUM2',
            example: 'SUM(1.1, 2)',
            tip: 'Adds two numbers together',
            icon: '',
            returns:
                {
                    type: Type.Number,
                    title: 'result',
                    tip: 'The result of adding the two arguments together'
                }
        })
        public sum2(@Param({title: 'value', tip: 'number', type: SheetType.AnyIValue}) args: IValue[]): ANumber {
            let sum: number = 0;
            for (let arg of args) {
                const argType = arg.getType();

                if (Array.isArray(arg)) {
                    for (let arrayElem of arg) {
                        if (arrayElem.isEqualTo(Type.Number))
                            sum += (arrayElem as ANumber).value;
                    }
                }
                else if (argType.isEqualTo(Type.Frame)) {
                    const allValues = (arg as IFrame).getAllValues();

                    allValues.map((v: IValue) => {
                        sum += (CoercionUtils.coerce(v, Type.Number, null) as ANumber).value;
                    });
                }
                else if (argType.isEqualTo(Type.Number))
                    sum += (arg as ANumber).value;
            }

            return ANumber.fromNumber(sum);
        }*/

    @Function({
        title: 'SUM(a,b)',
        alias: 'SUM',
        example: 'SUM(1.1, 2)',
        tip: 'Adds two numbers together',
        icon: '',
        returns:
            {
                type: Type.Number,
                title: 'result',
                tip: 'The result of adding the two arguments together',
            },
    })
    public sum(@Param({
        title: 'value',
        tip: 'number',
        type: SheetType.AnyIValue,
        repeatable: true,
    }) args:IValue[]):ANumber | AError
    {
        const result = MathFunctions._reduceNumbers(args, 0, (number, result) => number + result);
        if( Type.isInstanceOf(result, Type.Error) )
            return result as AError;

        return ANumber.fromNumber(result as number);
    }

    @Function({
        title: 'power(a,b)',
        tip: 'Computes a ^ b (takes a to the b-th power)',
        alias: 'POWER',
        example: 'POWER(4, 0.5)',
        icon: '',
        returns:
            {
                type: Type.Number,
                title: 'result',
                tip: 'The result of raising a ^ b'
            }
    })
    public power(@Param({title: 'a', tip: 'first number', type: Type.Number}) a:ANumber,
                 @Param({title: 'b', tip: 'second number', type: Type.Number}) b:ANumber):ANumber
    {
        return ANumber.fromNumber(Math.pow(a.value, b.value));
    }

    @Function({
        title: 'random()',
        tip: 'Computes random number',
        alias: 'RANDOM',
        example: 'RANDOM()',
        icon: '',
        returns:
            {
                type: Type.Number,
                title: 'result',
                tip: 'The random number'
            }
    })
    public random():ANumber
    {
        return ANumber.fromNumber(Math.random());
    }

    @Function({
        title: 'ROUND(value)',
        tip: 'Rounds number to the nearest whole number',
        alias: 'ROUND',
        example: 'ROUND(1.11)',
        icon: '',
        returns:
            {
                type: Type.Number,
                title: 'result',
                tip: 'The rounded, whole number'
            }
    })
    public round(@Param({title: 'value', tip: 'value to round', type: Type.Number})
                     value:ANumber):ANumber
    {
        return ANumber.fromNumber(Math.round(value.value));
    }

    @Function({
        title: 'ABS(value)',
        tip: 'Returns the absolute value of a number.',
        alias: 'ABS',
        example: 'ABS(-1.23)',
        icon: '',
        returns:
            {
                type: Type.Number,
                title: 'result',
                tip: 'The rounded, whole number'
            }
    })
    public abs(
        @Param({
            title: 'value',
            tip: 'The number of which to return the absolute value.',
            type: Type.Number
        }) value:ANumber):ANumber
    {
        return ANumber.fromNumber(Math.abs(value.value));
    }

    @Function({
        title: 'FLOOR(value)',
        tip: 'Rounds a number down to the nearest integer', // multiple of factor.',
        alias: 'FLOOR',
        example: 'FLOOR(1.55)', // 'FLOOR(1.55, 0.1)'
        icon: '',
        returns:
            {
                type: Type.Number,
                title: 'result',
                tip: 'The rounded, whole number'
            }
    })
    public floor(
        @Param({
            title: 'value',
            tip: 'The value to round down to the nearest integer', // multiple of specified significance factor.',
            type: Type.Number
        }) value:ANumber,
        // @Param({
        //     title: 'factor',
        //     tip: 'The number to whose multiples value will be rounded',
        //     type: Type.Number,
        //     optional: true,
        // }) factor:ANumber,
    ):ANumber
    {
        return ANumber.fromNumber(Math.floor(value.value));
    }

    @Function({
        title: 'CEILING(value)',
        tip: 'Rounds a number up to the nearest integer', // multiple of specified significance factor.',
        alias: 'CEILING',
        example: 'CEILING(1.45)', // 'CEILING(1.45, 0.1)'
        icon: '',
        returns:
            {
                type: Type.Number,
                title: 'result',
                tip: 'The rounded, whole number'
            }
    })
    public ceil(
        @Param({
            title: 'value',
            tip: 'The value to round up to the nearest integer', // multiple of factor.',
            type: Type.Number
        })value:ANumber,
        // @Param({
        //     title: 'factor',
        //     tip: 'The number to whose multiples value will be rounded.',
        //     type: Type.Number,
        //     optional: true,
        // }) factor:ANumber,
    ):ANumber
    {
        return ANumber.fromNumber(Math.ceil(value.value));
    }

    @Function({
        title: 'RANDBETWEEN(from,to)',
        tip: 'Returns a random integer within the specified range',
        alias: 'RANDBETWEEN',
        example: 'RANDBETWEEN(2,5)',
        icon: '',
        returns:
            {
                type: Type.Number,
                title: 'result',
                tip: 'The random number, always >= from and <= to'
            }
    })
    public randbetween(@Param({
                           title: 'from',
                           tip: 'minimal integer for random number range',
                           type: Type.Integer
                       }) from:AInteger,
                       @Param({
                           title: 'to',
                           tip: 'maximum integer for random number range',
                           type: Type.Integer
                       }) to:AInteger):ANumber | AError
    {
        if( !(from.value < to.value) )
            return AError.fromString('#INVALID_ARGS');

        const range = to.value - from.value + 1;
        const result = Math.floor(Math.random() * range) + from.value;

        return ANumber.fromNumber(result);
    }

    @Function({
        title: 'SERIES(initial_value, increment_by_value, num_values)',
        tip: 'Returns the series of numbers.',
        alias: 'SERIES',
        example: 'SERIES(1, 2, 3)',
        icon: '',
        returns:
            {
                type: Type.Frame,
                title: 'series',
                tip: 'The series of numbers.'
            }
    })

    @bind
    public series(
        @Param({title: 'initial_value', tip: 'The initial value.', type: Type.Number}) initial_value:ANumber,
        @Param({title: 'increment_by_value', tip: 'The incrementer.', type: Type.Number}) increment_by_value:ANumber,
        @Param({title: 'num_values', tip: 'The number of values.', type: Type.Number}) num_values:ANumber,
        @Param({
            title: 'column_name',
            tip: 'The name of column with values. The column name is "values" by default.',
            type: Type.String,
            optional: true
        }) column_name:AString
    ):IFrame
    {
        const nameOfResultColumn = column_name && Type.isInstanceOf(column_name, Type.String) ? column_name.value : 'values';
        const newFields:FrameField[] = [new FrameField(nameOfResultColumn, Type.Number)];

        const newRecords:IValue[][] = [];

        let value:number = initial_value.value;

        for( let recordIndex = 0; recordIndex < num_values.value; recordIndex++ )
        {
            newRecords.push([ANumber.fromNumber(value)]);
            value += increment_by_value.value;
        }

        return Frames.fromFieldsAndData(newFields, newRecords);
    }

    @Function({
        title: 'MOD(dividend, divisor)',
        tip: 'Returns the result of the modulo operator, the remainder after a division operation.',
        alias: 'MOD',
        example: 'MOD(10, 4)',
        icon: '',
        returns:
            {
                type: Type.Number,
                title: 'remainder',
                tip: 'The remainder after a division operation.'
            }
    })

    @bind
    public mod(
        @Param({title: 'dividend', tip: 'The initial value.', type: Type.Number}) dividend:ANumber,
        @Param({title: 'divisor', tip: 'The incrementer.', type: Type.Number}) divisor:ANumber
    ):ANumber
    {
        return ANumber.fromNumber(dividend.value % divisor.value);
    }

    @Function({
        title: 'COUNT(value, [value, ...])',
        alias: 'COUNT',
        example: 'COUNT(A2:A100,B2:B100,4,26)',
        tip: 'Returns a count of the number of numeric values in a dataset.',
        icon: '',
        returns:
            {
                type: Type.Number,
                title: 'result',
                tip: 'The count of the number of numeric values in a dataset',
            },
    })
    public count(@Param({
        title: 'value',
        tip: 'The value or range to consider when counting.',
        type: SheetType.AnyIValue,
        repeatable: true,
    }) args:IValue[]):ANumber | AError
    {
        const result = MathFunctions._reduceNumbers(args, 0, (number, result) => result + 1);
        if( Type.isInstanceOf(result, Type.Error) )
            return result as AError;

        return ANumber.fromNumber(result as number);
    }

    @Function({
        title: 'MIN(value, [value, ...])',
        alias: 'MIN',
        example: 'MIN(A2:A100,B2:B100,4,26)',
        tip: 'Returns the minimum value in a numeric dataset.',
        icon: '',
        returns:
            {
                type: Type.Number,
                title: 'result',
                tip: 'The minimum value in a numeric dataset',
            },
    })
    public min(@Param({
        title: 'value',
        tip: 'The value or range to consider when calculating the minimum value.',
        type: SheetType.AnyIValue,
        repeatable: true,
    }) args:IValue[]):ANumber | AError
    {
        const result = MathFunctions._reduceNumbers(args, Infinity, (number, result) => Math.min(number, result));
        if( Type.isInstanceOf(result, Type.Error) )
            return result as AError;

        return ANumber.fromNumber(result as number);
    }

    @Function({
        title: 'MAX(value, [value, ...])',
        alias: 'MAX',
        example: 'MAX(A2:A100,B2:B100,4,26)',
        tip: 'Returns the maximum value in a numeric dataset.',
        icon: '',
        returns:
            {
                type: Type.Number,
                title: 'result',
                tip: 'The maximum value in a numeric dataset',
            },
    })
    public max(@Param({
        title: 'value',
        tip: 'The value or range to consider when calculating the maximum value.',
        type: SheetType.AnyIValue,
        repeatable: true,
    }) args:IValue[]):ANumber | AError
    {

        const result = MathFunctions._reduceNumbers(args, -Infinity, (number, result) => Math.max(number, result));
        if( Type.isInstanceOf(result, Type.Error) )
            return result as AError;

        return ANumber.fromNumber(result as number);
    }

    @Function({
        title: 'AVERAGE(value, [value, ...])',
        alias: 'AVERAGE',
        example: 'AVERAGE(A2:A100,B2:B100,4,26)',
        tip: 'Returns the numerical average value in a dataset, ignoring text.',
        icon: '',
        returns:
            {
                type: Type.Number,
                title: 'result',
                tip: 'The numerical average value in a dataset'
            }
    })
    public avg(@Param({
        title: 'value',
        tip: 'The value or range to consider when calculating the average value.',
        type: SheetType.AnyIValue,
        repeatable: true
    }) args:IValue[]):ANumber | AError
    {
        const mathFunctions = new MathFunctions();

        const countResult = mathFunctions.count(args);

        if( Type.isInstanceOf(countResult, Type.Error) )
            return countResult;

        if( !Type.isInstanceOf(countResult, Type.Number) )
            return EvalErrors.Value;

        const countNumber = countResult as ANumber;
        if( !countNumber )
            return EvalErrors.Value;

        if( countNumber.value == 0 )
            return EvalErrors.Div0;

        const sumResult = mathFunctions.sum(args);

        if( Type.isInstanceOf(sumResult, Type.Error) )
            return sumResult;

        if( !Type.isInstanceOf(sumResult, Type.Number) )
            return EvalErrors.Value;

        const sumNumber = sumResult as ANumber;
        if( !sumNumber )
            return EvalErrors.Value;

        return ANumber.fromNumber(sumNumber.value / countNumber.value);
    }

    @Function({
        title: 'SORT(value, [value, ...])',
        alias: 'SORT',
        example: 'SORT(A1,"column1,column2",true)',
        tip: 'Sort frame using provided column(s)',
        icon: '',
        returns:
            {
                type: Type.Frame,
                title: 'result',
                tip: 'The numerical average value in a dataset'
            }
    })
    @bind
    public sort(@Param({
                    title: 'frame',
                    tip: 'Frame to sort',
                    type: Type.Frame
                }) frame:AFrame,
                @Param({
                    title: 'column_names',
                    tip: 'Column names to use for sorting',
                    type: Type.String
                }) columnNames:AString,
                @Param({
                    title: 'isDescending',
                    tip: 'true if frame should be sorted in descending order',
                    type: Type.Boolean,
                    optional: true
                }) isDescending:ABoolean):AFrame | AError
    {
        const matchType = isDescending && isDescending.value ? -1 : 1;
        if( null == columnNames || columnNames.value === '' )
        {
            return new AError('No column names provided');
        }
        const columns = columnNames.value.split(',');
        //check that columns exists in frame
        for( let i in columns )
        {
            let columnName = columns[i];
            if( !frame.findField(columnName) )
            {
                return new AError(`Cannot find \"${columnName}\" column`);
            }
        }
        let frameRecords = frame.getRecords().slice().sort((first, second) =>
        {
            for( let i in columns )
            {
                let columnName = columns[i];
                let comparisonResult = first.getCell(columnName).compareTo(second.getCell(columnName));
                if( (comparisonResult) !== 0 )
                {
                    return matchType * comparisonResult;
                }
            }
            return 0;
        });
        let aFrame = new AFrame(frame.getFields(), frameRecords.map(record => record.getCells()));
        return aFrame;
    }

    private static _reduceNumbers<R>(args:IValue[], initialValue:R, reducer:(number:number, result:R) => R):R | AError
    {
        let reduceResult:R = initialValue;
        const reduceNumber = (number:ANumber) =>
        {
            reduceResult = reducer(number.value, reduceResult);
        };

        for( let arg of args )
        {
            if( Type.isInstanceOf(arg, Type.Error) )
                return arg as AError;

            if( Array.isArray(arg) )
            {
                for( let arrayElem of arg )
                {
                    if( Type.isInstanceOf(arrayElem, Type.Error) )
                        return arrayElem;

                    if( Type.isInstanceOf(arrayElem, Type.Number) || Type.isInstanceOf(arrayElem, Type.Integer) )
                    {
                        const value = CoercionUtils.coerce(arrayElem, Type.Number, null);
                        if( Type.isInstanceOf(value, Type.Number) )
                        {
                            const aNumber = value as ANumber;
                            if( aNumber ) reduceNumber(aNumber);
                        }
                    }
                }
            }
            else if( Type.isInstanceOf(arg, Type.Frame) )
            {
                const allValues:IValue[] = (arg as IFrame).getAllValues();

                for( let i = 0; i < allValues.length; i++ )
                {
                    const item = allValues[i];

                    if( Type.isInstanceOf(item, Type.Error) )
                        return item as AError;

                    if( Type.isInstanceOf(item, Type.Number) || Type.isInstanceOf(item, Type.Integer) )
                    {
                        const value = CoercionUtils.coerce(item, Type.Number, null);
                        if( Type.isInstanceOf(value, Type.Number) )
                        {
                            const aNumber = value as ANumber;
                            if( aNumber ) reduceNumber(aNumber);
                        }
                    }
                }
            }
            else if( Type.isInstanceOf(arg, Type.Number) || Type.isInstanceOf(arg, Type.Integer) )
            {
                const value = CoercionUtils.coerce(arg, Type.Number, null);
                if( Type.isInstanceOf(value, Type.Number) )
                {
                    const aNumber = value as ANumber;
                    if( aNumber ) reduceNumber(aNumber);
                }
            }
        }

        return reduceResult;
    }
}
